/**
 * useAuditEvents — the firm's audit trail (Phase 4/5). Wraps
 * /v1/rbac/audit-events (firm.audit_view).
 */
import { useQuery } from '@tanstack/react-query';

import { config } from '../config';

export interface AuditEvent {
  id: string;
  actorUserId?: string | null;
  actorName?: string | null;
  action: string;
  resourceType: string;
  resourceId?: string | null;
  resourceName?: string | null;
  teamId?: string | null;
  createdAt: string;
}

export interface UseAuditEventsOptions {
  action?: string;
  /** Narrow to one resource's activity. resource_id is polymorphic, so pass
   *  resourceType alongside it (e.g. 'collection'). */
  resourceType?: string;
  resourceId?: string;
  /** Gate the fetch (e.g. only while a dialog is open). Defaults to true. */
  enabled?: boolean;
}

export const auditKeys = {
  all: ['rbac', 'audit-events'] as const,
  list: (action?: string, resourceType?: string, resourceId?: string) =>
    [...auditKeys.all, action ?? 'all', resourceType ?? 'all', resourceId ?? 'all'] as const,
};

/** The firm's audit trail, optionally narrowed to one resource. */
export function useAuditEvents(opts: UseAuditEventsOptions = {}) {
  const { action, resourceType, resourceId, enabled = true } = opts;
  return useQuery<AuditEvent[], Error>({
    queryKey: auditKeys.list(action, resourceType, resourceId),
    enabled,
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const url = new URL(`${config.API_URL}/v1/rbac/audit-events`);
      if (action) url.searchParams.set('action', action);
      if (resourceType) url.searchParams.set('resource_type', resourceType);
      if (resourceId) url.searchParams.set('resource_id', resourceId);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load audit trail (${res.status})`);
      return res.json();
    },
  });
}
